<template>
  <div>
    <!-- Delete Confirmation Dialog -->
    <el-dialog
      title="Confirm Delete"
      :visible.sync="showDeleteModal"
      width="400px">
      <p>Are you sure you want to delete this sampling definition?</p>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelDelete">Cancel</el-button>
        <el-button type="danger" @click="confirmDelete">Delete</el-button>
      </span>
    </el-dialog>

    <!-- Sampling Definitions Cards -->
    <div>
      <el-row v-for="(definitionRow, rowIndex) in definitionRows" :key="rowIndex">
        <el-col :span="6" v-for="(definition, colIndex) in definitionRow" :key="definition.id || 'add-card'">
          <!-- Regular Sampling Definition Card -->
          <el-card v-if="definition.id" class="box-card" style="margin:20px;">
            <div slot="header" class="clearfix">
              <span>
                <a :href="'/sampling/' + definition.id">
                  {{ definition.id }}
                </a>
              </span>
              <el-button
                style="float: right; padding: 3px 0"
                type="text"
                icon="el-icon-delete"
                @click.prevent="initiateDelete(definition)"
              ></el-button>
            </div>

            <div style="margin-bottom:20px;">
              <p>
                <strong>Method:</strong>
                <span v-if="definition.sampling_method === 1">Random</span>
                <span v-else-if="definition.sampling_method === 2">Import Order</span>
                <span v-else class="text-muted">Unknown</span>
              </p>
              <p>
                <strong>Size Limit Type:</strong>
                <span v-if="definition.max_size_method === 1">Count</span>
                <span v-else-if="definition.max_size_method === 2">Tokens</span>
                <span v-else class="text-muted">Unknown</span>
              </p>
              <p>
                <strong>Maximum Size:</strong>
                <span>{{ definition.max_size }}</span>
              </p>
            </div>

            <p style="font-size:smaller">
              <strong>Extent:</strong>
              <span v-if="definition.extent">
                {{ definition.extent.name }}
              </span>
              <span v-else class="text-muted">No Extent</span>
            </p>

            <p style="font-size:smaller;color:gray;">
              Last updated: {{ formatDate(definition.updated_at) }}
            </p>
          </el-card>

          <!-- Add Sampling Definition Card -->
          <el-card v-else class="box-card add-definition-card" style="margin:20px;">
            <div class="text-center py-4">
              <i class="fas fa-plus fa-3x text-muted mb-3"></i>
              <h5 class="text-muted mb-3">Add New Sampling Definition</h5>
              <button class="btn btn-primary" @click="showCreateModal = true">
                <i class="fas fa-plus"></i> Add Sampling Definition
              </button>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- Create Sampling Definition Modal -->
    <el-dialog
      title="Create New Sampling Definition"
      :visible.sync="showCreateModal"
      width="600px"
      @close="resetCreateForm">

      <FormManager
        v-if="showCreateModal"
        prettyEndPoint="/api/sampling/pretty/new"
        :initialFormValues="newDefinition"
        @save="createDefinition"
        @cancel="showCreateModal = false"
        :events="definitionCreateEvents"
        :edit_mode="true"
        :edit_mode_lock="true"
      />
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: "SamplingDefinitionsIndex",
  props: ['samplingDefinitions'],
  data() {
    return {
      localSamplingDefinitions: [...(this.samplingDefinitions || [])],
      showCreateModal: false,
      showDeleteModal: false,
      definitionToDelete: null,
      creating: false,
      newDefinition: {
        sampling_method: '',
        max_size_method: '',
        max_size: '',
        extent_id: '',
        filter_id: ''
      },
      definitionCreateEvents: [
        {
          label: "Create Sampling Definition",
          event: "save"
        },
        {
          label: "Cancel",
          event: "cancel"
        }
      ]
    }
  },
  computed: {
    definitionRows() {
      // Group definitions into rows of 4 for el-row/el-col layout, always include an "add" card
      if (!this.localSamplingDefinitions || this.localSamplingDefinitions.length === 0) {
        return [[{ id: null, isAddCard: true }]];
      }
      const definitionsWithAdd = [...this.localSamplingDefinitions, { id: null, isAddCard: true }];
      const rows = [];
      for (let i = 0; i < definitionsWithAdd.length; i += 4) {
        rows.push(definitionsWithAdd.slice(i, i + 4));
      }
      return rows;
    }
  },
  mounted() {
    console.log('Sampling Definitions:', this.samplingDefinitions);
    // Initialize localSamplingDefinitions with samplingDefinitions
    this.localSamplingDefinitions = [...(this.samplingDefinitions || [])];
  },
  watch: {
    // Watch for changes to samplingDefinitions prop
    samplingDefinitions: {
      handler(newVal) {
        this.localSamplingDefinitions = [...(newVal || [])];
      },
      deep: true
    }
  },
  methods: {
    goToDefinition(definitionId) {
      window.location.href = '/sampling/' + definitionId;
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown';
      const date = new Date(dateString);
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
    },

    resetCreateForm() {
      this.newDefinition = {
        sampling_method: '',
        max_size_method: '',
        max_size: '',
        extent_id: '',
        filter_id: ''
      };
      this.creating = false;
    },

    createDefinition(values) { // `values` comes from FormManager's @save event
      this.creating = true;
      console.log(values)

      axios.post('/api/sampling/extent/' + values.extent_id, values)
        .then(response => {
          this.$message.success('Sampling Definition created successfully');
          this.showCreateModal = false;
          this.resetCreateForm();

          // Add the newly created definition to the local list
          // Fetch the extent details if needed
          if (response.data && response.data.extent_id) {
            // If the response doesn't include the extent object, we need to find it
            const extentId = response.data.extent_id;
            const extent = this.localSamplingDefinitions.find(def => 
              def.extent && def.extent.id === extentId
            )?.extent;

            if (extent) {
              // If we found an extent with the same ID, use it
              response.data.extent = extent;
            }
          }

          // Add the new definition to the local list
          this.localSamplingDefinitions.push(response.data);
        })
        .catch(error => {
          console.error('Error creating sampling definition:', error);
          let errorMessage = 'Error creating sampling definition';
          if (error.response && error.response.data && error.response.data.message) {
            errorMessage += ': ' + error.response.data.message;
          }
          if (error.response && error.response.data && error.response.data.errors) {
            // Handle validation errors from Laravel if any slip through or are backend-specific
            for (const field in error.response.data.errors) {
              errorMessage += `\n- ${error.response.data.errors[field].join(', ')}`;
            }
          }
          this.$message.error(errorMessage);
          this.creating = false;
        });
    },

    initiateDelete(definition) {
      this.definitionToDelete = definition;
      this.showDeleteModal = true;
    },

    cancelDelete() {
      this.definitionToDelete = null;
      this.showDeleteModal = false;
    },

    confirmDelete() {
      if (!this.definitionToDelete) return;

      axios.delete(`/api/sampling/${this.definitionToDelete.id}`)
        .then(response => {
          this.$message.success('Sampling definition deleted successfully');

          // Remove the definition from the local array
          this.localSamplingDefinitions = this.localSamplingDefinitions.filter(
            d => d.id !== this.definitionToDelete.id
          );

          this.cancelDelete();
        })
        .catch(error => {
          console.error('Error deleting sampling definition:', error);
          this.$message.error('Error deleting sampling definition: ' + 
            (error.response?.data?.message || 'Unknown error'));
        });
    }
  }
}
</script>

<style scoped>
.text-muted {
  color: #6c757d;
  font-style: italic;
}
</style>
